import { useEffect, useRef, useState } from 'react';
import { loadScript, waitFor } from '../pickers/loadScript';
import { AuthRequestError, getAuthConfig, signInWithGoogle, type AuthState } from '../lib/auth';

/** The slice of Google Identity Services this screen uses. */
interface GoogleAccounts {
  id: {
    initialize(options: { client_id: string; callback: (r: { credential: string }) => void }): void;
    renderButton(parent: HTMLElement, options: Record<string, string | number>): void;
  };
}

// Identity Services and the Drive picker share one `window.google`. The picker
// declares that global, so reading the identity half through a narrow cast
// keeps the two from fighting over the same type.
function googleAccounts(): GoogleAccounts | undefined {
  return (window as unknown as { google?: { accounts?: GoogleAccounts } }).google?.accounts;
}

export function SignIn({ onSignedIn }: { onSignedIn: (state: AuthState) => void }) {
  const buttonRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [refused, setRefused] = useState(false);
  const [loading, setLoading] = useState(true);

  // onSignedIn is called from a Google callback that is registered once, so it
  // is read through a ref rather than re-registered on every render.
  const handler = useRef(onSignedIn);
  handler.current = onSignedIn;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { googleClientId, configured } = await getAuthConfig();
        if (!configured) {
          throw new Error('Sign-in is not configured on this server yet.');
        }

        await loadScript('https://accounts.google.com/gsi/client');
        const accounts = await waitFor(googleAccounts);
        if (cancelled || !buttonRef.current) return;

        accounts.id.initialize({
          client_id: googleClientId,
          callback: (response) => {
            setError(null);
            setRefused(false);
            signInWithGoogle(response.credential)
              .then((state) => handler.current(state))
              .catch((err: unknown) => {
                if (err instanceof AuthRequestError && err.status === 403) setRefused(true);
                setError(err instanceof Error ? err.message : 'Sign-in failed');
              });
          },
        });

        accounts.id.renderButton(buttonRef.current, {
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'pill',
          width: 260,
        });
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load Google sign-in');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="page">
      <header className="masthead">
        <h1>HEIC to JPG &amp; PNG</h1>
        <p>Convert iPhone photos to a format everything can open.</p>
      </header>

      <div className="card signin-card">
        <h2>Sign in to continue</h2>
        <p className="signin-lede">
          This converter is private. Sign in with Google so your photos stay yours and nobody else
          can see them.
        </p>

        {error ? (
          <div className={refused ? 'notice' : 'notice notice-error'}>
            {error}
            {refused ? (
              <div style={{ marginTop: 8 }}>
                Access is granted by the owner of this site. Once they add you, sign in again.
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="signin-button" ref={buttonRef} />
        {loading ? <div className="signin-lede">Loading Google sign-in…</div> : null}
      </div>

      <footer className="footer">
        Converted files are deleted automatically, and camera metadata including GPS location is
        stripped from every image.
      </footer>
    </div>
  );
}
